import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Avatar,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { toast } from "react-toastify";
import TaskUpdateDrawer from "../../bugherdComponents/TaskUpdateDrawer";
import {
  Snooze as SnoozeIcon,
  ArrowBack as ArrowBackIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetBugDetailByBugIdQuery,
  useUpdateBugDetailMutation,
  useGetListOfMembersQuery,
  useGetBugOccurrencesQuery,
} from "@/features/bughurd/api";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { colorPalette, getInitials } from "../../utils";
import AssigneeDrawer from "./AssigneeDrawer";
import DeleteModal from "@/Components/Common/delete/DeleteModal";
import BugDetailTabs from "./BugDetailTabs";
import { useTranslation } from "react-i18next";

dayjs.extend(relativeTime);
interface SystemBugDetailProps {
  bugId: string;
  parentData?: any;
}

const SystemBugDetail: React.FC<SystemBugDetailProps> = ({
  bugId,
  parentData,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: projectId } = useParams<{ id: string }>();

  // Add to Board drawer state - moved up to use in skip condition
  const [drawerOpen, setDrawerOpen] = useState(false);

  // api calls - skip when drawer is open to avoid duplicate fetches with TaskUpdateDrawer
  const {
    data: bugDetailData,
    isLoading,
    isFetching: isFetchingBugDetail,
    refetch: refetchBugDetail,
  } = useGetBugDetailByBugIdQuery(
    { bugId, withDetails: true },
    {
      skip: !bugId || drawerOpen,
    },
  );

  const [updateBug, { isLoading: updatingBug }] = useUpdateBugDetailMutation();

  // Extract bugDetailsData data from API response
  const bugDetailsData = useMemo(() => {
    return bugDetailData?.data?.decryptedData || bugDetailData?.data || null;
  }, [bugDetailData]);

  const { data: bugOccurrencesData } = useGetBugOccurrencesQuery(
    { projectId },
    {
      skip: !projectId,
      refetchOnMountOrArgChange: true,
    },
  );

  const bugIncident = bugDetailsData?.incidentAt || [];
  //   const bugIncident = [
  //   {
  //     "incidentAt": "2026-02-02T15:05:37.295Z",
  //     "ipAddress": "58.145.186.131",
  //     "count": 1
  //   },
  //   {
  //     "incidentAt": "2026-02-02T15:09:12.884Z",
  //     "ipAddress": "34.120.45.210",
  //     "count": 3
  //   },
  //   {
  //     "incidentAt": "2026-02-02T15:14:46.521Z",
  //     "ipAddress": "103.21.58.91",
  //     "count": 2
  //   }
  // ]
  // ;

//   const bugIncident = [
//   {
//     incidentAt: "2026-02-02T15:05:37.295Z",
//     ipAddress: "58.145.186.131",
//     count: 1
//   },
//   {
//     incidentAt: "2026-02-02T15:09:12.884Z",
//     ipAddress: "34.120.45.210",
//     count: 3
//   },
//   {
//     incidentAt: "2026-02-02T15:14:46.521Z",
//     ipAddress: "103.21.58.91",
//     count: 2
//   },
//   {
//     incidentAt: "2026-02-02T15:18:33.409Z",
//     ipAddress: "118.179.129.177",
//     count: 4
//   },
//   {
//     incidentAt: "2026-02-02T15:23:57.116Z",
//     ipAddress: "27.147.193.19",
//     count: 1
//   },
//   {
//     incidentAt: "2026-02-02T15:29:41.782Z",
//     ipAddress: "182.160.114.34",
//     count: 2
//   }
// ];

// const bugIncident = [
//   {
//     incidentAt: "2026-02-03T02:15:24.118Z",
//     ipAddress: "118.179.129.177", // Asia (Bangladesh)
//     count: 2
//   },
//   {
//     incidentAt: "2026-02-03T02:21:49.632Z",
//     ipAddress: "81.169.145.77", // Europe (Germany)
//     count: 1
//   },
//   {
//     incidentAt: "2026-02-03T02:28:05.904Z",
//     ipAddress: "34.120.45.210", // North America (USA)
//     count: 4
//   },
//   {
//     incidentAt: "2026-02-03T02:34:41.287Z",
//     ipAddress: "186.192.90.55", // South America (Brazil)
//     count: 1
//   },
//   {
//     incidentAt: "2026-02-03T02:41:18.763Z",
//     ipAddress: "41.75.112.98", // Africa (Kenya)
//     count: 3
//   }
// ];



  // console.log(bugIncident, "bugDetailsDatabugDetailsData");
  // console.log(
  //   bugOccurrencesData?.data?.decryptedData,
  //   "bugOccurrencesDatabugOccurrencesData",
  // );

  const [topTabValue, setTopTabValue] = useState(0);

  // Snooze dialog state
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);

  // Assignee drawer state
  const [assigneeDrawerOpen, setAssigneeDrawerOpen] = useState(false);

  // Fetch project members for assignee drawer
  const { data: projectMembersData } = useGetListOfMembersQuery({
    projectId: projectId,
    page: 1,
    size: 100,
    searchName: "",
  });

  const projectMembers = useMemo(() => {
    const memberItems = projectMembersData?.data?.decryptedData?.items || [];
    // Filter by role and map to required format
    return memberItems
      .filter(
        (item: any) =>
          item?.userRole === "TEAM_MEMBER" || item?.userRole === "TEAM_LEAD",
      )
      .map((item: any) => ({
        _id: item?.user?._id || item?._id,
        fullName:
          `${item?.user?.firstName || ""} ${item?.user?.lastName || ""}`.trim(),
        email: item?.user?.email,
        userRole: item?.userRole,
      }));
  }, [projectMembersData]);

  // Handle assignee drawer open
  const handleAssigneeClick = () => {
    setAssigneeDrawerOpen(true);
  };

  // Handle assignee save
  const handleAssigneeSave = async (selectedUserIds: string[]) => {
    await updateBug({
      bugId: bugId,
      assignTo: selectedUserIds,
    }).unwrap();
    refetchBugDetail();
    toast.success("Assignees updated successfully");
  };

  // Handle snooze toggle
  const handleSnoozeToggle = async () => {
    try {
      const newSnoozeState = !bugDetailsData?.isSnoozed;
      await updateBug({
        bugId: bugId,
        isSnoozed: newSnoozeState,
      }).unwrap();
      // need to refetch details api here
      refetchBugDetail();
      toast.success(
        newSnoozeState
          ? "Bug snoozed successfully"
          : "Bug unsnoozed successfully",
      );
      setSnoozeDialogOpen(false);
    } catch (error) {
      toast.error("Failed to update snooze status");
    }
  };

  // Handle Add to Board click
  const handleAddToBoard = () => {
    // setSelectedBugId(bugId);
    // navigate(
    //   `/defect-tracker/projects/${projectId}/system-bugs/${bugId}?bugId=${bugId}`,
    // );
    setDrawerOpen(true);
  };

  // Helper function to format chart data for different time periods
  const formatChartData = (
    period: "lastHour" | "lastDay" | "lastTwoWeeks" | "lastTwoMonths",
  ) => {
    const periodData = bugOccurrencesData?.data?.decryptedData?.[period];
    if (!periodData || !periodData.hourlyData) {
      return { categories: [], data: [], total: 0 };
    }

    const categories = periodData.hourlyData.map((item: any) => {
      const date = dayjs(item.hour);
      // Format based on period
      if (period === "lastHour") {
        return date.format("h:mm A"); // e.g., "1:00 PM"
      } else if (period === "lastDay") {
        return date.format("h A"); // e.g., "1 PM"
      } else if (period === "lastTwoWeeks") {
        return date.format("MMM D"); // e.g., "Jan 29"
      } else {
        return date.format("MMM D"); // e.g., "Jan 29"
      }
    });

    const data = periodData.hourlyData.map((item: any) => item.count);
    const total = periodData.total || 0;

    return { categories, data, total };
  };

  const timeAgo = (date: string) => {
    return dayjs(date).fromNow();
  };

  // Format incident data for timeline chart
  const formatIncidentChartData = () => {
    const incidents = bugIncident;
    if (!incidents || incidents.length === 0) {
      return { categories: [], data: [], incidents: [] };
    }

    // Group incidents by hour for better visualization
    const incidentMap = new Map<string, { count: number; ips: string[] }>();

    incidents.forEach((incident: any) => {
      const hour = dayjs(incident.incidentAt).format("MMM D, h A");
      const existing = incidentMap.get(hour);
      if (existing) {
        existing.count += 1;
        if (!existing.ips.includes(incident.ipAddress)) {
          existing.ips.push(incident.ipAddress);
        }
      } else {
        incidentMap.set(hour, { count: 1, ips: [incident.ipAddress] });
      }
    });

    // Convert to arrays for chart
    const categories = Array.from(incidentMap.keys());
    const data = Array.from(incidentMap.values()).map((v) => v.count);

    return { categories, data, incidents };
  };

  const handleTopTabChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setTopTabValue(newValue);
  };

  const handleBack = () => {
    navigate(`/defect-tracker/projects/${projectId}/system-bugs`);
  };

  if (isLoading || isFetchingBugDetail) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1300, // above content
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!bugDetailsData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Bug not found</Typography>
        <Button onClick={handleBack}>Go Back</Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        overflow: "auto",
        bgcolor: "background.default",
      }}
    >
      {/* Header with Action Buttons */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderBottom: `1px solid ${theme.palette.divider}`,
          p: "10px",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <IconButton onClick={handleBack}>
          <ArrowBackIcon />
        </IconButton>

        {/*   "isSnoozed": false, */}
        <Button
          variant={bugDetailsData?.isSnoozed ? "contained" : "outlined"}
          startIcon={<SnoozeIcon />}
          size="small"
          onClick={() => setSnoozeDialogOpen(true)}
          disabled={updatingBug}
        >
          {bugDetailsData?.isSnoozed ? t("snoozed") : t("snooze")}
        </Button>
        <Button
          variant="outlined"
          endIcon={<ErrorIcon />}
          size="small"
          sx={{ color: "#EB5B00" }}
        >
          {t("error")}
        </Button>
        <Box
          sx={{
            ml: "auto",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            padding: "4px 8px",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            "&:hover": { bgcolor: "action.hover" },
          }}
          onClick={handleAssigneeClick}
        >
          {!bugDetailsData?.assignTo ||
          bugDetailsData?.assignTo?.length === 0 ? (
            <>
              <PersonAddOutlinedIcon
                sx={{ fontSize: 18, color: "text.secondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                {t("Unassigned")}
              </Typography>
            </>
          ) : (
            <>
              {bugDetailsData?.assignTo
                ?.slice(0, 3)
                ?.map((user: any, index: number) => (
                  <Tooltip
                    key={user._id || index}
                    title={user.fullName || "Unknown"}
                    arrow
                  >
                    <Avatar
                      sx={{
                        backgroundColor:
                          colorPalette[index % colorPalette.length],
                        width: 24,
                        height: 24,
                        fontSize: 11,
                        fontWeight: 500,
                        marginRight: "-6px",
                        border: `1px solid ${theme.palette.background.paper}`,
                        zIndex: 10 - index,
                      }}
                    >
                      {getInitials(user.fullName || "")}
                    </Avatar>
                  </Tooltip>
                ))}
              {bugDetailsData?.assignTo?.length > 3 && (
                <Avatar
                  sx={{
                    backgroundColor: "#ccc",
                    width: 24,
                    height: 24,
                    fontSize: 11,
                    fontWeight: 500,
                    border: `1px solid ${theme.palette.background.paper}`,
                    ml: 1,
                  }}
                >
                  +{bugDetailsData?.assignTo?.length - 3}
                </Avatar>
              )}
            </>
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DashboardIcon />}
          onClick={handleAddToBoard}
        >
          {t("addToBoard")}
        </Button>
      </Box>

      {/* Bug Title and Info */}
      <Box sx={{ bgcolor: "background.paper", p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: "#FFA500", width: 32, height: 32 }}>JS</Avatar>
          <Typography variant="h5" fontWeight={600}>
            #{bugDetailsData.bugKey} {bugDetailsData.summary}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("latest")}</strong> {timeAgo(bugDetailsData.updatedAt)}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            <strong>{t("Total")}:</strong> {bugDetailsData.count || "N/A"}
          </Typography>
          {/* <Typography variant="body2" color="text.secondary">
            <strong>{t("ips")}</strong> {bugDetailsData?.ipAddress || "N/A"}
          </Typography> */}
          <Typography variant="body2" color="text.secondary">
            <strong>{t("autoBugCountLabel")}</strong>{" "}
            {bugDetailsData?.autoBugCount || "N/A"}
          </Typography>
        </Box>

        {/* Top Tabs */}
        <Tabs value={topTabValue} onChange={handleTopTabChange}>
          <Tab label={t("occurrences")} />
          {/* <Tab label="Affected IPs" /> */}
          {/* <Tab label="Affected People" /> */}
        </Tabs>
      </Box>

      {/* Charts Section */}
      <Box sx={{ bgcolor: "background.paper", p: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 2,
          }}
        >
          {/* Last hour chart */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                {t("lastHour")}
              </Typography>
              <Typography variant="h4" color="primary" gutterBottom>
                {formatChartData("lastHour").total}
              </Typography>
              {parentData?.chartWrapper ? (
                <parentData.chartWrapper
                  options={{
                    chart: {
                      type: "bar",
                      sparkline: { enabled: false },
                      toolbar: { show: false },
                    },
                    plotOptions: { bar: { columnWidth: "50%" } },
                    dataLabels: { enabled: false },
                    colors: ["#D91656"],
                    xaxis: {
                      categories: formatChartData("lastHour").categories,
                      tickAmount: Math.min(
                        formatChartData("lastHour").categories.length - 1,
                        6,
                      ),
                      labels: { show: true, style: { fontSize: "10px" } },
                    },
                    yaxis: {
                      labels: { show: true, style: { fontSize: "10px" } },
                    },
                    grid: { show: true, borderColor: theme.palette.divider },
                  }}
                  series={[
                    {
                      name: t("occurrences"),
                      data: formatChartData("lastHour").data,
                    },
                  ]}
                  type="bar"
                  height={120}
                  width="100%"
                />
              ) : null}
            </CardContent>
          </Card>

          {/* Last day chart */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                {t("lastDay")}
              </Typography>
              <Typography variant="h4" color="primary" gutterBottom>
                {formatChartData("lastDay").total}
              </Typography>
              {parentData?.chartWrapper ? (
                <parentData.chartWrapper
                  options={{
                    chart: {
                      type: "bar",
                      sparkline: { enabled: false },
                      toolbar: { show: false },
                    },
                    plotOptions: { bar: { columnWidth: "50%" } },
                    dataLabels: { enabled: false },
                    colors: ["#D91656"],
                    xaxis: {
                      categories: formatChartData("lastDay").categories,
                      tickAmount: Math.min(
                        formatChartData("lastDay").categories.length - 1,
                        8,
                      ),
                      labels: { show: true, style: { fontSize: "10px" } },
                    },
                    yaxis: {
                      labels: { show: true, style: { fontSize: "10px" } },
                    },
                    grid: { show: true, borderColor: theme.palette.divider },
                  }}
                  series={[
                    {
                      name: t("occurrences"),
                      data: formatChartData("lastDay").data,
                    },
                  ]}
                  type="bar"
                  height={120}
                  width="100%"
                />
              ) : null}
            </CardContent>
          </Card>

          {/* Last two weeks chart */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                {t("lastTwoWeeks")}
              </Typography>
              <Typography variant="h4" color="primary" gutterBottom>
                {formatChartData("lastTwoWeeks").total}
              </Typography>
              {parentData?.chartWrapper ? (
                <parentData.chartWrapper
                  options={{
                    chart: {
                      type: "bar",
                      sparkline: { enabled: false },
                      toolbar: { show: false },
                    },
                    plotOptions: { bar: { columnWidth: "50%" } },
                    dataLabels: { enabled: false },
                    colors: ["#D91656"],
                    xaxis: {
                      categories: formatChartData("lastTwoWeeks").categories,
                      tickAmount: Math.min(
                        formatChartData("lastTwoWeeks").categories.length - 1,
                        7,
                      ),
                      labels: {
                        show: true,
                        style: { fontSize: "10px" },
                        rotate: -45,
                      },
                    },
                    yaxis: {
                      labels: { show: true, style: { fontSize: "10px" } },
                    },
                    grid: { show: true, borderColor: theme.palette.divider },
                  }}
                  series={[
                    {
                      name: t("occurrences"),
                      data: formatChartData("lastTwoWeeks").data,
                    },
                  ]}
                  type="bar"
                  height={120}
                  width="100%"
                />
              ) : null}
            </CardContent>
          </Card>

          {/* Last two months chart */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                {t("lastTwoMonths")}
              </Typography>
              <Typography variant="h4" color="primary" gutterBottom>
                {formatChartData("lastTwoMonths").total}
              </Typography>
              {parentData?.chartWrapper ? (
                <parentData.chartWrapper
                  options={{
                    chart: {
                      type: "bar",
                      sparkline: { enabled: false },
                      toolbar: { show: false },
                    },
                    plotOptions: { bar: { columnWidth: "50%" } },
                    dataLabels: { enabled: false },
                    colors: ["#D91656"],
                    xaxis: {
                      categories: formatChartData("lastTwoMonths").categories,
                      tickAmount: Math.min(
                        formatChartData("lastTwoMonths").categories.length - 1,
                        7,
                      ),
                      labels: {
                        show: true,
                        style: { fontSize: "10px" },
                        rotate: -45,
                      },
                    },
                    yaxis: {
                      labels: { show: true, style: { fontSize: "10px" } },
                    },
                    grid: { show: true, borderColor: theme.palette.divider },
                  }}
                  series={[
                    {
                      name: t("occurrences"),
                      data: formatChartData("lastTwoMonths").data,
                    },
                  ]}
                  type="bar"
                  height={120}
                  width="100%"
                />
              ) : null}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Incident Timeline Chart */}
      {bugIncident?.length > 0 && (
        <Box sx={{ bgcolor: "background.paper", p: 3, mt: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t("Incident Timeline")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: 2,
            }}
          >
            {/* Timeline Chart */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  {t("Incidents Over Time")}
                </Typography>
                <Typography variant="h4" color="primary" gutterBottom>
                  {bugIncident?.length || 0}
                </Typography>
                {parentData?.chartWrapper ? (
                  <parentData.chartWrapper
                    options={{
                      chart: {
                        type: "area",
                        sparkline: { enabled: false },
                        toolbar: { show: false },
                      },
                      stroke: {
                        curve: "smooth",
                        width: 2,
                      },
                      fill: {
                        type: "gradient",
                        gradient: {
                          shadeIntensity: 1,
                          opacityFrom: 0.7,
                          opacityTo: 0.3,
                        },
                      },
                      colors: ["#2196F3"],
                      xaxis: {
                        categories: formatIncidentChartData().categories,
                        labels: {
                          show: true,
                          style: { fontSize: "10px" },
                          rotate: -45,
                        },
                      },
                      yaxis: {
                        labels: {
                          show: true,
                          style: { fontSize: "10px" },
                        },
                      },
                      grid: { show: true, borderColor: theme.palette.divider },
                      tooltip: {
                        x: { show: true },
                        y: {
                          formatter: (val: number) => `${val} incident(s)`,
                        },
                      },
                    }}
                    series={[
                      {
                        name: t("Incidents"),
                        data: formatIncidentChartData().data,
                      },
                    ]}
                    type="area"
                    height={250}
                    width="100%"
                  />
                ) : null}
              </CardContent>
            </Card>

            {/* Incident Details */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  {t("Incident Details")}
                </Typography>
                <Box
                  sx={{
                    maxHeight: 280,
                    overflow: "auto",
                    mt: 2,
                  }}
                >
                  {bugIncident?.map((incident: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        borderLeft: "3px solid #2196F3",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {dayjs(incident.incidentAt).format(
                          "MMM D, YYYY h:mm A",
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ mt: 0.5 }}
                      >
                        IP: {incident.ipAddress}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Bottom Section with Tabs */}
      <BugDetailTabs
        bugDetailsData={bugDetailsData}
        bugIncident={bugIncident}
      />

      {/* Snooze Confirmation Modal */}
      {snoozeDialogOpen && (
        <DeleteModal
          open={snoozeDialogOpen}
          onCancel={() => setSnoozeDialogOpen(false)}
          onDelete={handleSnoozeToggle}
          confirmMessage={
            bugDetailsData?.isSnoozed
              ? "Are you sure you want to unsnooze this bug?"
              : "Are you sure you want to snooze this bug?"
          }
          isDeleting={updatingBug}
          successName={bugDetailsData?.isSnoozed ? "Unsnoozed" : "Snoozed"}
        />
      )}

      {/* Assignee Drawer */}
      <AssigneeDrawer
        open={assigneeDrawerOpen}
        onClose={() => setAssigneeDrawerOpen(false)}
        users={projectMembers}
        currentAssignees={bugDetailsData?.assignTo || []}
        onSave={handleAssigneeSave}
      />

      {/* Add to Board Drawer */}
      {drawerOpen && (
        <TaskUpdateDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          setBugId={() => {}}
          setDrawerOpen={() => {}}
          bugId={bugId}
          users={projectMembers}
          createdBy={bugDetailsData?.createdBy?.fullName || ""}
          setCreatedBy={() => {}}
          bugFor="BOARD"
          onSuccess={() =>
            navigate(`/defect-tracker/projects/${projectId}/system-bugs`)
          }
        />
      )}
    </Box>
  );
};

export default SystemBugDetail;
